/**
 * Shared "export with progress toast" flow: spinner toast → monotonic
 * percentage updates from the archive builder → download → success/error
 * toast. Used by the Tools-pane export buttons and the Scene Explorer
 * context-menu exports so the two surfaces can't drift.
 */

import type { ZipProgress } from 'trace-viewer/scene';
import { showSpinnerToast } from '../toast';
import { downloadBlob } from './download-blob';

/**
 * Run `getBlob` behind a progress toast and download the result as
 * `fileName`. The archive builds in a worker, so progress callbacks arrive
 * while the editor stays responsive. Progress is clamped monotonic — a
 * worker-failure fallback rebuilds from 0% and would otherwise make the
 * displayed percentage jump backward.
 */
export async function exportWithProgressToast(
    fileName: string,
    getBlob: (onProgress: (p: ZipProgress) => void) => Promise<Blob>,
): Promise<void> {
    const dismiss = showSpinnerToast(`Exporting ${fileName}…`);
    let lastPct = -1;
    try {
        const blob = await getBlob((p) => {
            const pct = Math.round(p.fraction * 100);
            if (pct > lastPct) {
                lastPct = pct;
                dismiss.update(`Exporting ${fileName}… ${pct}%`);
            }
        });
        downloadBlob(blob, fileName);
        dismiss(`Exported ${fileName} (${(blob.size / 1e6).toFixed(1)} MB)`, 'success');
    } catch (error) {
        console.error(`[Export] ${fileName} export failed:`, error);
        const msg = error instanceof Error ? error.message : String(error);
        dismiss(`Export failed: ${msg}`, 'error');
    }
}
